<script setup lang="ts">
import type { FinancingDetailsViewModel } from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/useReceivableValues'
import type { ReceiptTransfer } from '~/utils/receipt-model/ReceiptModel'
import { SolDivider } from '@solfacil/girassol'
import IconAutorenew from '~icons/material-symbols/autorenew'

const props = withDefaults(defineProps<{
  details: FinancingDetailsViewModel
  downloadingKitReceipt?: boolean
}>(), {
  downloadingKitReceipt: false,
})

const emit = defineEmits<{
  (e: 'downloadReceipt', transfer: ReceiptTransfer): void
}>()

const { t } = useI18n()
const { formatMoney } = useMoney()

const EMPTY_VALUE = '-'

function formatValue(value: number | null): string {
  return value === null ? EMPTY_VALUE : formatMoney(value)
}

function handleKitReceiptClick() {
  const transfer = props.details.equipment?.receiptTransfer
  if (transfer && !props.downloadingKitReceipt)
    emit('downloadReceipt', transfer)
}
</script>

<template>
  <div class="flex flex-col gap-4 p-2xs bg-neutral-high-pure">
    <div class="flex items-center justify-between gap-2">
      <span class="fonts-body-medium-bold text-neutral-low-medium">
        {{ t('receipt-model.receivables.details.financed_value') }}
      </span>
      <span class="fonts-body-medium-bold shrink-0 whitespace-nowrap text-neutral-low-dark">
        {{ formatValue(details.financingValue) }}
      </span>
    </div>

    <template v-if="details.equipment">
      <SolDivider thickness="x-small" orientation="horizontal" />

      <section class="flex flex-col gap-2">
        <h4 class="financing-details__title">
          {{ t('receipt-model.receivables.details.equipment') }}
        </h4>
        <dl class="flex flex-col gap-2">
          <div class="financing-details__item">
            <dt>{{ t(details.equipment.labelKey, details.equipment.labelParams) }}</dt>
            <dd class="financing-details__item-value">
              {{ formatValue(details.equipment.value) }}
            </dd>
          </div>
        </dl>
        <button
          v-if="details.equipment.receiptTransfer"
          id="transaction-download-equipment"
          type="button"
          class="financing-details__receipt"
          :disabled="downloadingKitReceipt"
          :aria-busy="downloadingKitReceipt"
          :aria-label="t('receipt-model.receivables.view_receipt_aria', { title: t(details.equipment.labelKey, details.equipment.labelParams) })"
          @click="handleKitReceiptClick"
        >
          <IconAutorenew v-if="downloadingKitReceipt" class="animate-spin shrink-0" aria-hidden="true" />
          {{ t('receipt-model.receivables.view_receipt') }}
        </button>
      </section>
    </template>

    <SolDivider thickness="x-small" orientation="horizontal" />

    <section class="flex flex-col gap-2">
      <h4 class="financing-details__title">
        {{ t(details.servicesTitleKey) }}
      </h4>
      <dl class="flex flex-col gap-2">
        <div v-for="line in details.services" :key="line.key" class="financing-details__item">
          <dt>{{ t(line.labelKey, line.labelParams) }}</dt>
          <dd class="financing-details__item-value">
            {{ formatValue(line.value) }}
          </dd>
        </div>
      </dl>
    </section>
  </div>
</template>

<style scoped lang="scss">
.financing-details__title {
  @apply fonts-body-small-bold text-neutral-low-medium;
}

.financing-details__item {
  @apply flex items-start justify-between gap-3;
  @apply fonts-body-small-regular text-neutral-low-light;
}

.financing-details__item-value {
  @apply shrink-0 whitespace-nowrap;
}

.financing-details__receipt {
  @apply inline-flex items-center gap-1 self-end whitespace-nowrap;
  @apply fonts-body-small-bold underline text-neutral-low-dark;

  &:hover:not(:disabled) {
    @apply opacity-75;
  }

  &:disabled {
    @apply cursor-wait;
  }
}
</style>
